<template>
  <div class="container mx-auto px-4 py-8 bg-gray-900">
    <div class="max-w-4xl mx-auto bg-gray-800 shadow rounded-lg p-6">
      <!-- Available Tags Section -->
      <div class="mb-6 p-4 bg-gray-700 rounded-lg">
        <h2 class="text-xl font-bold text-white mb-3">Available Template Tags</h2>
        <div class="space-y-2 font-mono text-sm text-gray-300">
          <p class="mt-4">Service Conditional Tags:</p>
          <ul class="list-disc pl-6 space-y-1">
            <template v-for="service in servicesList" :key="service.id">
              <li>
                <code>{#{{ service.title }}}</code>
                <span class="text-gray-400"> - Start {{ service.title }} section</span>
              </li>
              <li class="ml-4">
                <code>{/{{ service.title }}}</code>
                <span class="text-gray-400"> - End {{ service.title }} section</span>
              </li>
            </template>
          </ul>
          <p class="mt-4 text-sm text-gray-400">
            Note: Place your methodology content between the start and end tags.
            The section will only appear if the service is selected.
          </p>
        </div>
      </div>

      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-white">Services Management</h1>
        <FwbButton
          @click="addNewService"
          class="!bg-emerald-600 hover:!bg-emerald-700 text-white"
        >
          Add Service
        </FwbButton>
      </div>

      <draggable 
        v-model="servicesList"
        class="space-y-4"
        handle=".drag-handle"
        item-key="id"
        @end="handleDragEnd"
      >
        <template #item="{ element: service }">
          <div class="bg-gray-700 p-4 rounded-lg">
            <div class="flex items-center space-x-2">
              <div class="drag-handle cursor-move p-2 text-gray-400 hover:text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-move"><polyline points="5 9 2 12 5 15"></polyline><polyline points="9 5 12 2 15 5"></polyline><polyline points="15 19 12 22 9 19"></polyline><polyline points="19 9 22 12 19 15"></polyline><line x1="2" y1="12" x2="22" y2="12"></line><line x1="12" y1="2" x2="12" y2="22"></line></svg>
              </div>
              
              <div class="flex-grow space-y-4">
                <div>
                  <label class="block text-sm font-medium text-white mb-2">
                    Service Name
                  </label>
                  <FwbInput
                    v-model="service.name"
                    class="!bg-gray-600 border-gray-500 text-white"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-white mb-2">
                    Template Tag
                  </label>
                  <p class="text-xs text-gray-400 mb-2">
                    This will be used as the template tag for the service: {#tag}{/tag}
                  </p>
                  <FwbInput
                    v-model="service.title"
                    class="!bg-gray-600 border-gray-500 text-white"
                    placeholder="Enter title without spaces or special characters"
                  />
                </div>

                <div class="flex justify-end space-x-2 mt-4">
                  <FwbButton
                    @click="removeService(service)"
                    class="!bg-red-600 hover:!bg-red-700 text-white w-10 h-10 !p-0 flex items-center justify-center"
                    :disabled="isProcessing"
                    title="Remove Service"
                  >
                    <TrashIcon />
                  </FwbButton>
                </div>
              </div>
            </div>
          </div>
        </template>
      </draggable>

      <div class="flex py-4 justify-end items-center mb-6">
        <FwbButton
          @click="addNewService"
          class="!bg-emerald-600 hover:!bg-emerald-700 text-white"
        >
          Add Service
        </FwbButton>
      </div>
      <div class="flex justify-end mt-6 space-x-4">
        <FwbButton
          v-if="error"
          @click="error = ''"
          class="!bg-red-600 hover:!bg-red-700 text-white"
        >
          Clear Error
        </FwbButton>
        <FwbButton
          @click="saveChanges"
          class="!bg-emerald-600 hover:!bg-emerald-700 text-white"
        >
          Save Changes
        </FwbButton>
      </div>

      <div v-if="error" class="mt-4 text-red-500">
        {{ error }}
      </div>
      <div v-if="success" class="mt-4 text-green-500">
        Changes saved successfully!
      </div>

      <!-- Error Toast -->
      <div v-if="error" 
        class="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg"
        style="z-index: 1000;"
      >
        {{ error }}
      </div>
      
      <!-- Success Toast -->
      <div v-if="success" 
        class="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg"
        style="z-index: 1000;"
      >
        {{ success }}
      </div>

      <!-- Delete Confirmation Modal -->
      <FwbModal
        v-if="showDeleteModal"
        @close="showDeleteModal = false"
        size="md"
        class="
          [&_.modal-content]:!bg-gray-700 
          [&_.modal-content]:!text-white 
          [&_.modal-overlay]:!bg-gray-900/90
          [&_.relative]:!bg-gray-700
          [&_button.text-gray-400]:!text-gray-300
          [&_button.hover\:bg-gray-200]:!hover:bg-gray-600
          [&_button.hover\:text-gray-900]:!hover:text-white
          [&_.border-gray-200]:!border-gray-600
          [&_.bg-white]:!bg-gray-700
          [&_.dark\:bg-gray-700]:!bg-gray-700
          [&_.dark\:border-gray-600]:!border-gray-600
        "
      >
        <template #header>
          <div class="border-b border-gray-600 px-4 py-2.5">
            <h3 class="text-xl font-medium text-gray-200">
              Confirm Delete
            </h3>
          </div>
        </template>
        <template #body>
          <div class="px-4 py-2.5">
            <p class="text-gray-200">
              Are you sure you want to delete "{{ serviceToDelete?.name }}"?<br>
              This action cannot be undone.
            </p>
          </div>
        </template>
        <template #footer>
          <div class="border-t border-gray-600 px-4 py-2.5">
            <div class="flex justify-end space-x-2">
              <FwbButton
                class="!bg-gray-600 hover:!bg-gray-700 text-white"
                @click="showDeleteModal = false"
              >
                Cancel
              </FwbButton>
              <FwbButton
                class="!bg-red-600 hover:!bg-red-700 text-white"
                :disabled="isProcessing"
                @click="confirmDelete"
              >
                {{ isProcessing ? 'Deleting...' : 'Delete' }}
              </FwbButton>
            </div>
          </div>
        </template>
      </FwbModal>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { FwbButton, FwbInput, FwbAlert, FwbModal } from 'flowbite-vue';
import draggable from 'vuedraggable';
import TrashIcon from './icons/TrashIcon.vue';

export default {
  name: 'ServicesAdmin',
  components: {
    FwbButton,
    FwbInput,
    FwbAlert,
    draggable,
    FwbModal,
    TrashIcon
  },
  setup() {
    const store = useStore();
    const services = computed(() => store.state.services);
    const isProcessing = ref(false);
    const error = ref('');
    const success = ref('');
    const showDeleteModal = ref(false);
    const serviceToDelete = ref(null);

    const servicesList = computed({
      get: () => Array.isArray(services.value) ? services.value : [],
      set: (value) => {
        store.commit('SET_SERVICES', value);
      }
    });

    const loadServices = async () => {
      try {
        await store.dispatch('fetchServices');
      } catch (err) {
        error.value = 'Failed to load services';
      }
    };

    const addNewService = () => {
      const newService = {
        id: Date.now(), // Temporary ID
        name: 'New Service',
        title: '',
        display_order: servicesList.value.length
      };
      store.commit('SET_SERVICES', [...servicesList.value, newService]);
    };

    const removeService = async (service) => {
      try {
        serviceToDelete.value = service;
        showDeleteModal.value = true;
      } catch (err) {
        error.value = err.message || 'Failed to remove service';
      }
    };

    const confirmDelete = async () => {
      try {
        isProcessing.value = true;
        await store.dispatch('deleteService', serviceToDelete.value.id);
        
        const index = servicesList.value.findIndex(s => s.id === serviceToDelete.value.id);
        if (index > -1) {
          const updatedServices = [...servicesList.value];
          updatedServices.splice(index, 1);
          updatedServices.forEach((s, i) => {
            s.display_order = i;
          });
          store.commit('SET_SERVICES', updatedServices);
          await store.dispatch('updateServices', updatedServices);
        }
        success.value = 'Service removed successfully';
        setTimeout(() => {
          success.value = '';
        }, 3000);
        showDeleteModal.value = false;
        serviceToDelete.value = null;
      } catch (err) {
        error.value = err.message || 'Failed to remove service';
        setTimeout(() => {
          error.value = '';
        }, 3000);
      } finally {
        isProcessing.value = false;
      }
    };

    const handleDragEnd = () => {
      const updatedServices = [...servicesList.value];
      updatedServices.forEach((service, index) => {
        service.display_order = index;
      });
      store.commit('SET_SERVICES', updatedServices);
    };

    const saveChanges = async () => {
      try {
        await store.dispatch('updateServices', servicesList.value);
        success.value = true;
        setTimeout(() => {
          success.value = false;
        }, 3000);
      } catch (err) {
        error.value = err.message || 'Failed to save changes';
      }
    };

    onMounted(loadServices);

    return {
      servicesList,
      error,
      success,
      addNewService,
      removeService,
      handleDragEnd,
      saveChanges,
      isProcessing,
      showDeleteModal,
      serviceToDelete,
      confirmDelete
    };
  }
};
</script>

<style>
.drag-handle {
  touch-action: none;
}
</style> 